import React, { useEffect, useState } from "react";

const API = process.env.REACT_APP_API_BASE_URL ?? "https://zoompay.co.ke";
const getToken = () => localStorage.getItem("adminToken");

const ACTION_LABELS = {
  change_user_role:     "Changed user role",
  process_settlement:   "Processed settlement",
  simulate_payout:      "Simulated payout",
  mark_settlement_paid: "Marked settlement paid",
  edit_fx_discount:     "Edited FX discount",
  edit_fx_rate:         "Edited FX rate",
  refresh_fx_rates:     "Refreshed FX rates",
};

const ACTION_OPTIONS = Object.keys(ACTION_LABELS);

export default function CockpitAudit() {
  const [logs,    setLogs]    = useState([]);
  const [total,   setTotal]   = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter,  setFilter]  = useState({ action: "", actor: "" });
  const [offset,  setOffset]  = useState(0);
  const [selected, setSelected] = useState(null);
  const limit = 50;

  const load = async () => {
    setLoading(true);
    const q = new URLSearchParams({
      limit,
      offset,
      ...Object.fromEntries(Object.entries(filter).filter(([, v]) => v)),
    });
    try {
      const res = await fetch(`${API}/api/v2/cockpit/audit-logs?${q}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const d = await res.json();
      if (d.ok) { setLogs(d.logs || []); setTotal(d.total || 0); }
    } catch (e) {
      // swallow; table will show empty
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, offset]);

  const fmtDate = (s) => {
    try { return new Date(s).toLocaleString(); } catch { return s; }
  };

  const statusBadge = (s) => {
    const base = "px-2 py-0.5 rounded text-xs font-semibold";
    if (s === "success") return <span className={`${base} bg-green-100 text-green-700`}>success</span>;
    return <span className={`${base} bg-red-100 text-red-700`}>{s}</span>;
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Audit Log</h1>
          <p className="text-sm text-gray-500">Record of all sensitive cockpit actions — money movements, pricing, and privilege changes.</p>
        </div>
        <div className="text-sm text-gray-500">{total} total {total === 1 ? "entry" : "entries"}</div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <select
          value={filter.action}
          onChange={(e) => { setOffset(0); setFilter(f => ({ ...f, action: e.target.value })); }}
          className="border border-gray-300 rounded px-3 py-2 text-sm"
        >
          <option value="">All actions</option>
          {ACTION_OPTIONS.map(a => <option key={a} value={a}>{ACTION_LABELS[a]}</option>)}
        </select>
        <input
          type="text"
          placeholder="Filter by actor email…"
          value={filter.actor}
          onChange={(e) => { setOffset(0); setFilter(f => ({ ...f, actor: e.target.value })); }}
          className="border border-gray-300 rounded px-3 py-2 text-sm flex-1 min-w-[200px]"
        />
        <button
          onClick={() => load()}
          className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-4 py-2 rounded"
        >
          Refresh
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600 text-left">
            <tr>
              <th className="px-4 py-3">When</th>
              <th className="px-4 py-3">Actor</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Action</th>
              <th className="px-4 py-3">Target</th>
              <th className="px-4 py-3">IP</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">Loading…</td></tr>
            ) : logs.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">No audit entries found.</td></tr>
            ) : logs.map(log => (
              <tr key={log.id} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-3 whitespace-nowrap text-gray-700">{fmtDate(log.created_at)}</td>
                <td className="px-4 py-3 text-gray-800">{log.actor_email || "—"}</td>
                <td className="px-4 py-3 text-gray-500">{log.actor_role || "—"}</td>
                <td className="px-4 py-3 font-medium text-gray-800">{ACTION_LABELS[log.action] || log.action}</td>
                <td className="px-4 py-3 text-gray-600">
                  {log.target_type ? `${log.target_type}${log.target_id ? ` #${String(log.target_id).substring(0,8)}` : ""}` : "—"}
                </td>
                <td className="px-4 py-3 text-gray-500">{log.ip_address || "—"}</td>
                <td className="px-4 py-3">{statusBadge(log.status)}</td>
                <td className="px-4 py-3">
                  {log.detail && Object.keys(log.detail).length > 0 && (
                    <button onClick={() => setSelected(log)} className="text-indigo-600 hover:underline text-xs">Details</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4 text-sm">
        <button
          disabled={offset === 0}
          onClick={() => setOffset(Math.max(0, offset - limit))}
          className={`px-3 py-1.5 rounded border ${offset === 0 ? "text-gray-300 border-gray-200" : "text-gray-700 border-gray-300 hover:bg-gray-50"}`}
        >
          ← Previous
        </button>
        <span className="text-gray-500">
          {total === 0 ? "0" : offset + 1}–{Math.min(offset + limit, total)} of {total}
        </span>
        <button
          disabled={offset + limit >= total}
          onClick={() => setOffset(offset + limit)}
          className={`px-3 py-1.5 rounded border ${offset + limit >= total ? "text-gray-300 border-gray-200" : "text-gray-700 border-gray-300 hover:bg-gray-50"}`}
        >
          Next →
        </button>
      </div>

      {/* Detail modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">Audit Detail</h3>
              <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <div className="space-y-2 text-sm">
              <div><span className="text-gray-500">Action:</span> <span className="font-medium">{ACTION_LABELS[selected.action] || selected.action}</span></div>
              <div><span className="text-gray-500">Actor:</span> {selected.actor_email} ({selected.actor_role})</div>
              <div><span className="text-gray-500">When:</span> {fmtDate(selected.created_at)}</div>
              <div><span className="text-gray-500">IP:</span> {selected.ip_address || "—"}</div>
              <div><span className="text-gray-500">Target:</span> {selected.target_type || "—"} {selected.target_id || ""}</div>
              <div className="pt-2">
                <span className="text-gray-500">Detail:</span>
                <pre className="bg-gray-50 rounded p-3 mt-1 text-xs overflow-auto max-h-60">{JSON.stringify(selected.detail, null, 2)}</pre>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
